/* eslint-disable unicode-bom */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AlertModal from './AlertModal';
import ConfirmModal from './ConfirmModal';
import StatisticsManagersTable, { DayPillStrip } from './StatisticsManagersTable';
import { ServerIcon } from '../utils/serverIcons';
import {
  MRP_METRICS,
  getMetricsForSection,
  calcActivityScore,
  calcWeekDelta,
  getDayMetric,
  getMetricTotal,
} from '../utils/mrpStatisticsMetrics';
import { SENIOR_AND_ABOVE } from '../constants/rolePermissions';
import {
  getCurrentWeekStartDate,
  weekStartToStr,
  readStatsSearchParams,
  buildStatsSearchParams,
} from '../utils/statisticsUrlParams';
import './StatisticsMobile.css';
import './StatisticsManagers.css';
import './StatisticsManagersExtended.css';
import './StatisticsJunior.css';

const getCurrentWeekStart = getCurrentWeekStartDate;

const StatisticsJunior = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlRef = useRef(null);
  if (!initialUrlRef.current) {
    initialUrlRef.current = readStatsSearchParams(
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : searchParams
    );
  }
  const initialUrl = initialUrlRef.current;
  /**
   * Выбор из ссылки или клика в сессии.
   * В URL moderator при клике не пишем → после F5 карточка не откроется сама.
   */
  const selectedModeratorIdRef = useRef(
    initialUrl.moderatorId ? String(initialUrl.moderatorId) : null
  );
  const weekStatsLoadIdRef = useRef(0);

  const activityRailRef = useRef(null);
  const [moderators, setModerators] = useState([]);
  const [selectedModerator, setSelectedModerator] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [weekStart, setWeekStart] = useState(
    () => initialUrl.weekStart || getCurrentWeekStart()
  );
  const [weekStats, setWeekStats] = useState(null);
  const [allModeratorsStats, setAllModeratorsStats] = useState([]);
  const [displayMode, setDisplayMode] = useState(
    () => (initialUrl.displayMode === 'table' ? 'table' : 'list')
  );
  const [tableViewDay, setTableViewDay] = useState('week');
  const [editingMetric, setEditingMetric] = useState(null);
  const [metricEditValue, setMetricEditValue] = useState('');
  const [savingMetric, setSavingMetric] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [vacations, setVacations] = useState([]);
  const [servers, setServers] = useState([]);
  const [callConfirmOpen, setCallConfirmOpen] = useState(false);
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error',
  });

  const resetMetricEditState = useCallback(() => {
    setEditingMetric(null);
    setMetricEditValue('');
  }, []);

  const getWeekStartStr = useCallback(() => weekStartToStr(weekStart), [weekStart]);

  const loadUserRoles = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/check-roles', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUserRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
    }
  }, []);

  const loadServers = useCallback(async () => {
    try {
      const response = await fetch('/api/servers', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setServers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading servers:', error);
    }
  }, []);

  const loadModerators = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/statistics/junior/moderators?weekStartDate=${getWeekStartStr()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          cache: 'no-store',
        },
      );
      if (!response.ok) {
        console.error('Error loading junior moderators: HTTP', response.status);
        return;
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setModerators(list);
      const wantedId = selectedModeratorIdRef.current;
      if (wantedId) {
        const mod = list.find((m) => String(m.discord_id) === String(wantedId)) || null;
        setSelectedModerator(mod);
        if (!mod) {
          selectedModeratorIdRef.current = null;
          setWeekStats(null);
        }
      } else {
        setSelectedModerator((current) => {
          if (!current) return null;
          return list.find((m) => String(m.discord_id) === String(current.discord_id)) || null;
        });
      }
    } catch (error) {
      console.error('Error loading junior moderators:', error);
    } finally {
      setLoading(false);
    }
  }, [getWeekStartStr]);

  const loadWeekStats = useCallback(async () => {
    const discordId = selectedModerator?.discord_id || selectedModeratorIdRef.current;
    if (!discordId) return;
    const loadId = ++weekStatsLoadIdRef.current;
    try {
      setLoading(true);
      const response = await fetch(
        `/api/statistics/junior/week?moderatorDiscordId=${encodeURIComponent(String(discordId))}&weekStartDate=${getWeekStartStr()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          cache: 'no-store',
        },
      );
      if (loadId !== weekStatsLoadIdRef.current) return;
      if (!response.ok) {
        console.error('Error loading junior week stats: HTTP', response.status);
        return;
      }
      const data = await response.json();
      if (loadId !== weekStatsLoadIdRef.current) return;
      setWeekStats(data);
      resetMetricEditState();
    } catch (error) {
      if (loadId === weekStatsLoadIdRef.current) {
        console.error('Error loading junior week stats:', error);
      }
    } finally {
      if (loadId === weekStatsLoadIdRef.current) setLoading(false);
    }
  }, [selectedModerator?.discord_id, getWeekStartStr, resetMetricEditState]);

  const loadAllModeratorsStats = useCallback(async () => {
    try {
      setLoadingTable(true);
      const response = await fetch(
        `/api/statistics/junior/week/all?weekStartDate=${getWeekStartStr()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          cache: 'no-store',
        },
      );
      if (response.ok) {
        const data = await response.json();
        setAllModeratorsStats(data);
        resetMetricEditState();
      }
    } catch (error) {
      console.error('Error loading all junior stats:', error);
    } finally {
      setLoadingTable(false);
    }
  }, [getWeekStartStr, resetMetricEditState]);

  const loadVacations = useCallback(async () => {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    try {
      const response = await fetch(
        `/api/vacations?startDate=${weekStartToStr(start)}&endDate=${weekStartToStr(end)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      if (response.ok) setVacations(await response.json());
    } catch (error) {
      console.error('Error loading vacations:', error);
    }
  }, [weekStart]);

  useEffect(() => {
    loadUserRoles();
    loadServers();
  }, [loadUserRoles, loadServers]);

  useEffect(() => {
    loadModerators();
  }, [loadModerators]);

  useEffect(() => {
    if (displayMode === 'table') {
      loadAllModeratorsStats();
      return;
    }
    if (selectedModerator?.discord_id) {
      loadWeekStats();
    } else {
      setWeekStats(null);
    }
  }, [selectedModerator?.discord_id, displayMode, loadWeekStats, loadAllModeratorsStats]);

  useEffect(() => {
    loadVacations();
  }, [loadVacations]);

  // В URL только неделя и режим
  useEffect(() => {
    const next = buildStatsSearchParams({
      weekStartStr: weekStartToStr(weekStart),
      moderatorId: null,
      displayMode: isMobile ? 'list' : displayMode,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [weekStart, displayMode, isMobile, searchParams, setSearchParams]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      setIsMobile(mobile);
      if (mobile) setDisplayMode('list');
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const canEdit = SENIOR_AND_ABOVE.some((role) => userRoles.includes(role));

  const handleStartEditMetric = useCallback((moderatorId, day, field, currentValue) => {
    setEditingMetric({ moderatorId, day, field });
    setMetricEditValue(currentValue > 0 ? String(currentValue) : '');
  }, []);

  const handleSaveMetric = useCallback(async (moderatorId, dayOfWeek, field) => {
    if (savingMetric) return;
    let value = metricEditValue.trim();
    if (value === '' || value === '0') {
      value = 0;
    } else {
      value = parseInt(value, 10);
      if (Number.isNaN(value) || value < 0 || value > 999) {
        resetMetricEditState();
        return;
      }
    }

    try {
      setSavingMetric(true);
      const response = await fetch('/api/statistics/junior/metric', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moderatorDiscordId: moderatorId,
          weekStartDate: getWeekStartStr(),
          dayOfWeek,
          field,
          value,
        }),
      });
      if (response.ok) {
        resetMetricEditState();
        if (displayMode === 'list' && selectedModerator?.discord_id === moderatorId) {
          await loadWeekStats();
        } else if (displayMode === 'table') {
          await loadAllModeratorsStats();
        }
      } else {
        const error = await response.json();
        setAlertModal({
          isOpen: true,
          title: 'Ошибка',
          message: error.error || 'Ошибка при сохранении',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving junior metric:', error);
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Ошибка при сохранении',
        type: 'error',
      });
    } finally {
      setSavingMetric(false);
    }
  }, [
    savingMetric,
    metricEditValue,
    getWeekStartStr,
    resetMetricEditState,
    displayMode,
    selectedModerator,
    loadWeekStats,
    loadAllModeratorsStats,
  ]);

  const getDayOverDayDelta = useCallback((dayStat, metricKey) => {
    const days = weekStats?.dailyStats || [];
    const value = getDayMetric(dayStat, metricKey);
    const previous = days.find((item) => item.day === dayStat.day - 1);
    if (!previous) return null;
    const prevValue = getDayMetric(previous, metricKey);
    if (!value && !prevValue) return null;
    return calcWeekDelta(value, prevValue);
  }, [weekStats]);

  const renderMetricCellList = useCallback((moderatorId, dayStat, metric) => {
    const value = getDayMetric(dayStat, metric.key);
    const isEditing = canEdit
      && editingMetric?.moderatorId === moderatorId
      && editingMetric?.day === dayStat.day
      && editingMetric?.field === metric.key;
    const dayDelta = !isEditing ? getDayOverDayDelta(dayStat, metric.key) : null;

    if (isEditing) {
      return (
        <input
          type="number"
          min="0"
          max="999"
          value={metricEditValue}
          onChange={(event) => {
            const nextValue = event.target.value;
            if (nextValue === '' || (parseInt(nextValue, 10) >= 0 && parseInt(nextValue, 10) <= 999 && nextValue.length <= 3)) {
              setMetricEditValue(nextValue);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              if (!savingMetric) handleSaveMetric(moderatorId, dayStat.day, metric.key);
            }, 150);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.target.blur();
            if (event.key === 'Escape') resetMetricEditState();
          }}
          className="statistics-mrp-ext-input"
          autoFocus
          placeholder="0"
        />
      );
    }

    return (
      <div className="junior-day-metric">
        <button
          type="button"
          className={`statistics-mrp-ext-value ${value > 0 ? 'has-value' : 'is-empty'} ${canEdit ? 'is-editable' : ''}`}
          onClick={() => canEdit && handleStartEditMetric(moderatorId, dayStat.day, metric.key, value)}
          disabled={!canEdit}
        >
          {value > 0 ? value : '—'}
        </button>
        {dayDelta && (
          <span
            className={`junior-day-delta junior-day-delta--${dayDelta.direction}`}
            title={`К предыдущему дню: ${dayDelta.diff > 0 ? '+' : ''}${dayDelta.diff} (${dayDelta.percent > 0 ? '+' : ''}${dayDelta.percent}%)`}
          >
            {dayDelta.direction === 'up' ? '▲' : dayDelta.direction === 'down' ? '▼' : '●'}
            {dayDelta.diff > 0 ? '+' : ''}{dayDelta.diff}
          </span>
        )}
      </div>
    );
  }, [
    canEdit,
    editingMetric,
    metricEditValue,
    savingMetric,
    handleSaveMetric,
    handleStartEditMetric,
    resetMetricEditState,
    getDayOverDayDelta,
  ]);

  const serverIconMap = useMemo(() => {
    const result = {};
    servers.forEach((server) => {
      result[`RU${server.server_number}_${server.server_name}`] = server.icon_url || null;
    });
    return result;
  }, [servers]);

  const juniorModerators = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return moderators.filter((moderator) => {
      const isJunior = (moderator.role || '').toLowerCase().includes('junior');
      const matches = !query
        || (moderator.nickname || '').toLowerCase().includes(query)
        || (moderator.server || '').toLowerCase().includes(query)
        || (moderator.discord_id || '').toLowerCase().includes(query);
      return isJunior && matches;
    });
  }, [moderators, searchQuery]);

  const tableModerators = useMemo(() => [...allModeratorsStats]
    .filter((moderator) => (moderator.role || '').toLowerCase().includes('junior'))
    .sort((a, b) => {
      const aNumber = Number((a.server || '').match(/^RU(\d+)/i)?.[1]) || 9999;
      const bNumber = Number((b.server || '').match(/^RU(\d+)/i)?.[1]) || 9999;
      return aNumber - bNumber;
    }), [allModeratorsStats]);

  const listViewMetrics = useMemo(() => getMetricsForSection(false), []);

  const weekDayHeaders = useMemo(() => {
    const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    return dayNames.map((name, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return `${name} - ${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
    });
  }, [weekStart]);

  const isVacationDay = useCallback((moderatorDiscordId, date) => vacations.some(
    (vacation) => vacation.moderator_discord_id === moderatorDiscordId && vacation.vacation_date === date,
  ), [vacations]);

  // Junior учится 4–5 дней: сравниваем новый день со средним прошлых, а не неделю к неделе
  const dayCompare = useMemo(() => {
    const days = (weekStats?.dailyStats || []).map((dayStat) => {
      const totals = Object.fromEntries(
        MRP_METRICS.map((metric) => [metric.key, Number(dayStat[metric.key]) || 0]),
      );
      return {
        day: dayStat.day,
        date: dayStat.date,
        totals,
        score: calcActivityScore(totals),
      };
    });

    const worked = days.filter((day) => day.score > 0);
    const latest = worked[worked.length - 1] || null;
    const earlier = worked.slice(0, -1);
    const avgEarlierScore = earlier.length
      ? Math.round(earlier.reduce((sum, day) => sum + day.score, 0) / earlier.length)
      : 0;
    const previousDay = earlier[earlier.length - 1] || null;
    const currentScore = latest?.score || 0;

    const cards = MRP_METRICS.map((metric) => {
      const series = days.map((day) => Number(day.totals[metric.key]) || 0);
      const workedSeries = worked.map((day) => Number(day.totals[metric.key]) || 0);
      const value = workedSeries[workedSeries.length - 1] || 0;
      const earlierValues = workedSeries.slice(0, -1);
      const avgEarlier = earlierValues.length
        ? Math.round(earlierValues.reduce((sum, item) => sum + item, 0) / earlierValues.length)
        : 0;
      return {
        ...metric,
        value,
        series,
        delta: calcWeekDelta(value, avgEarlier),
        max: Math.max(1, ...series),
        share: currentScore > 0 ? Math.round((value / currentScore) * 100) : 0,
      };
    });

    return {
      ready: Boolean(weekStats?.dailyStats?.length),
      currentScore,
      delta: calcWeekDelta(currentScore, avgEarlierScore),
      vsPrevDay: calcWeekDelta(currentScore, previousDay?.score || 0),
      hasEarlier: earlier.length > 0,
      latestDay: latest?.day || null,
      chronological: days,
      maxScore: Math.max(1, ...days.map((day) => day.score || 0)),
      cards,
    };
  }, [weekStats]);

  const tableMetricProps = useMemo(() => ({
    weekDayHeaders,
    canEdit,
    editingCell: editingMetric,
    editValue: metricEditValue,
    savingMetric,
    onStartEdit: handleStartEditMetric,
    onEditValueChange: setMetricEditValue,
    onSaveEdit: handleSaveMetric,
    onCancelEdit: resetMetricEditState,
    isVacationDay,
    serverIconMap,
    formatDuration: (minutes) => {
      const value = Number(minutes) || 0;
      return value >= 60 ? `${Math.floor(value / 60)}ч ${value % 60}м` : `${value}м`;
    },
    viewDay: tableViewDay,
    onViewDayChange: setTableViewDay,
  }), [
    weekDayHeaders,
    canEdit,
    editingMetric,
    metricEditValue,
    savingMetric,
    handleStartEditMetric,
    handleSaveMetric,
    resetMetricEditState,
    isVacationDay,
    serverIconMap,
    tableViewDay,
  ]);

  const formatWeekRange = () => {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${String(start.getDate()).padStart(2, '0')}.${String(start.getMonth() + 1).padStart(2, '0')} - ${String(end.getDate()).padStart(2, '0')}.${String(end.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatDayDate = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}.${month}.${year}`;
  };

  const formatDayName = (day) => ['', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'][day] || '';
  const formatDayNameShort = (day) => ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][day] || '';
  const formatProfileDate = (value) => value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString('ru-RU')
    : null;
  const getAvatarLetter = (name) => name ? name.charAt(0).toUpperCase() : '?';
  const isCurrentWeek = Math.abs(weekStart.getTime() - getCurrentWeekStart().getTime()) < 24 * 60 * 60 * 1000;

  const changeWeek = (days) => {
    const nextWeek = new Date(weekStart);
    nextWeek.setDate(nextWeek.getDate() + days);
    setWeekStart(nextWeek);
  };

  const scrollActivityRail = (direction) => {
    activityRailRef.current?.scrollBy({
      left: direction * (isMobile ? 156 : 208),
      behavior: 'smooth',
    });
  };

  const renderToolbar = () => (
    <div className="statistics-mrp-toolbar">
      <div className="statistics-mrp-toolbar-start">
        {isMobile && (
          <div className="statistics-mrp-mobile-top-row">
            <button type="button" className="statistics-mrp-selector-moderator-btn" onClick={() => setMobileSidebarOpen(true)}>
              {selectedModerator ? (
                <>
                  <div className="statistics-mrp-mobile-select-avatar">{getAvatarLetter(selectedModerator.nickname)}</div>
                  <div className="statistics-mrp-mobile-select-info">
                    <div className="statistics-mrp-mobile-select-name">{selectedModerator.nickname}</div>
                    <div className="statistics-mrp-mobile-select-role">{selectedModerator.role || 'Junior'}</div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </>
              ) : <span>Модератор</span>}
            </button>
          </div>
        )}
        <div className="statistics-mrp-week-switcher">
          <button type="button" className="statistics-mrp-week-switcher-btn" onClick={() => changeWeek(-7)} aria-label="Предыдущая неделя">‹</button>
          <button
            type="button"
            className={`statistics-mrp-week-switcher-label ${isCurrentWeek ? 'is-current' : ''}`}
            onClick={!isCurrentWeek ? () => setWeekStart(getCurrentWeekStart()) : undefined}
          >
            {formatWeekRange()}
          </button>
          <button type="button" className="statistics-mrp-week-switcher-btn" onClick={() => changeWeek(7)} aria-label="Следующая неделя">›</button>
        </div>
      </div>
      {displayMode === 'table' && !isMobile && (
        <div className="statistics-mrp-toolbar-center">
          <DayPillStrip weekDayHeaders={weekDayHeaders} activeDay={tableViewDay} onSelect={setTableViewDay} />
        </div>
      )}
      <div className="statistics-mrp-toolbar-end">
        <div className="statistics-mrp-toolbar-segment">
          <button type="button" className={displayMode === 'list' ? 'active' : ''} onClick={() => setDisplayMode('list')} disabled={isMobile} title="Списком">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
          </button>
          <button type="button" className={displayMode === 'table' ? 'active' : ''} onClick={() => setDisplayMode('table')} disabled={isMobile} title="Таблицей">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
          </button>
        </div>
      </div>
    </div>
  );

  const renderSidebar = () => (
    <aside className={`statistics-mrp-sidebar ${isMobile && mobileSidebarOpen ? 'mobile-open' : ''}`}>
      <div className="statistics-mrp-sidebar-header">
        <div className="statistics-mrp-sidebar-heading">
          <h2>Junior</h2>
          <span className="statistics-mrp-sidebar-count">{juniorModerators.length}</span>
        </div>
        <button type="button" className="statistics-mrp-sidebar-close" onClick={() => setMobileSidebarOpen(false)} aria-label="Закрыть">×</button>
      </div>
      <div className="statistics-mrp-search">
        <input type="search" placeholder="Поиск по нику..." value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
      </div>
      <div className="statistics-mrp-moderators-list">
        {juniorModerators.length > 0 ? (
          <div className="statistics-mrp-moderator-category">
            <div className="statistics-mrp-category-title">
              <span>Junior</span>
              <span className="statistics-mrp-category-count">{juniorModerators.length}</span>
            </div>
            {juniorModerators.map((moderator) => (
              <button
                type="button"
                key={moderator.discord_id}
                className={`statistics-mrp-moderator-item ${selectedModerator?.discord_id === moderator.discord_id ? 'selected' : ''} ${moderator.deleted_at ? 'deleted' : ''}`}
                onClick={() => {
                  const discordId = String(moderator.discord_id);
                  if (
                    selectedModeratorIdRef.current === discordId &&
                    selectedModerator?.discord_id === discordId
                  ) {
                    setMobileSidebarOpen(false);
                    return;
                  }
                  selectedModeratorIdRef.current = discordId;
                  setWeekStats(null);
                  setSelectedModerator(moderator);
                  setMobileSidebarOpen(false);
                }}
              >
                <div className="statistics-mrp-moderator-avatar">
                  <ServerIcon
                    iconUrl={serverIconMap[moderator.server]}
                    size={18}
                    fallback={getAvatarLetter(moderator.nickname)}
                  />
                </div>
                <div className="statistics-mrp-moderator-info">
                  <div className="statistics-mrp-moderator-name">
                    {moderator.nickname || '—'}
                    {moderator.deleted_at ? <span className="statistics-mrp-deleted-badge">удалён</span> : null}
                  </div>
                  <div className="statistics-mrp-moderator-role">{(moderator.server || '').replace('_', ' · ') || moderator.discord_id}</div>
                </div>
              </button>
            ))}
          </div>
        ) : <div className="statistics-mrp-empty">Модераторы не найдены</div>}
      </div>
    </aside>
  );

  if (loading && !weekStats && moderators.length === 0) {
    return (
      <div className="statistics-mrp-loading">
        <div className="loader" />
        <p>Загрузка данных...</p>
      </div>
    );
  }

  return (
    <div className={`statistics-mrp-container ${displayMode === 'table' ? 'table-mode' : ''}`}>
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ isOpen: false, title: '', message: '', type: 'error' })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
      <ConfirmModal
        isOpen={callConfirmOpen}
        onClose={() => setCallConfirmOpen(false)}
        onConfirm={() => {
          setCallConfirmOpen(false);
          navigate('/discordmoderation/junior-calls', { state: { moderator: selectedModerator } });
        }}
        title="Начать обзвон"
        message={`Вы действительно хотите начать обзвон — опрос для модератора ${selectedModerator?.nickname || ''}?`}
        confirmLabel="Начать"
        cancelLabel="Отмена"
      />
      {isMobile && mobileSidebarOpen && displayMode === 'list' && (
        <div className="statistics-mrp-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}
      {displayMode === 'list' && renderSidebar()}
      <main className="statistics-mrp-main">
        {renderToolbar()}
        {displayMode === 'list' && !selectedModerator ? (
          <div className="statistics-mrp-empty-state">
            <svg className="statistics-mrp-empty-state-icon" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <p>Выберите модератора для просмотра статистики</p>
          </div>
        ) : null}

        {displayMode === 'list' && selectedModerator && !weekStats ? (
          <div className="statistics-mrp-loading" style={{ minHeight: 240 }}>
            <div className="loader"></div>
            <p>Загрузка статистики...</p>
          </div>
        ) : null}

        {displayMode === 'list' && selectedModerator && weekStats && (
          <div key={selectedModerator.discord_id} className="statistics-mrp-content mrp-list">
            <div className="mrp-list__head">
              <div className="mrp-list__identity">
                <ServerIcon
                  iconUrl={serverIconMap[weekStats.moderator.server || selectedModerator.server]}
                  size={isMobile ? 28 : 34}
                  className="mrp-list__icon"
                  fallback={
                    <span className="mrp-list__icon-fallback">
                      {getAvatarLetter(weekStats.moderator.nickname || selectedModerator.nickname)}
                    </span>
                  }
                />
              </div>
              <div className="mrp-list__meta">
                <div className="mrp-list__meta-cell">
                  <span className="mrp-list__meta-label">Ник</span>
                  <div className="mrp-list__meta-top">
                    <span className="mrp-list__meta-value mrp-list__meta-value--name">
                      {weekStats.moderator.nickname || '—'}
                    </span>
                    {weekStats.moderator.deleted_at ? (
                      <span className="mrp-list__badge mrp-list__badge--deleted">удалён</span>
                    ) : null}
                  </div>
                </div>
                <div className="mrp-list__meta-sep" aria-hidden />
                <div className="mrp-list__meta-cell">
                  <span className="mrp-list__meta-label">Angry Space</span>
                  <span className="mrp-list__meta-value mrp-list__meta-value--as">
                    {weekStats.moderator.angry_space || selectedModerator.angry_space || '—'}
                  </span>
                </div>
                <div className="mrp-list__meta-sep" aria-hidden />
                <div className="mrp-list__meta-cell">
                  <span className="mrp-list__meta-label">Роль</span>
                  <span className="mrp-list__meta-value mrp-list__meta-value--role">
                    {weekStats.moderator.role || 'Junior'}
                  </span>
                </div>
                <div className="mrp-list__meta-sep" aria-hidden />
                <div className="mrp-list__meta-cell">
                  <span className="mrp-list__meta-label">В команде</span>
                  <span className="mrp-list__meta-value">
                    {weekStats.moderator.created_at
                      ? `с ${formatProfileDate(weekStats.moderator.created_at)}`
                      : '—'}
                  </span>
                  {weekStats.moderator.restored_at && !weekStats.moderator.deleted_at ? (
                    <span className="mrp-list__meta-note mrp-list__meta-note--ok">
                      восст. {formatProfileDate(weekStats.moderator.restored_at)}
                    </span>
                  ) : null}
                  {weekStats.moderator.deleted_at ? (
                    <span className="mrp-list__meta-note mrp-list__meta-note--gone">
                      удалён {formatProfileDate(weekStats.moderator.deleted_at)}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="mrp-list__actions">
                <button type="button" className="mrp-list__export statistics-junior-call-btn" onClick={() => setCallConfirmOpen(true)}>
                  Начать обзвон
                </button>
              </div>
            </div>

            <div className="mrp-list__insight" aria-label="Активность по дням">
              {!dayCompare.ready ? (
                <div className="mrp-list__insight-empty">Нет данных по активности</div>
              ) : (
                <div className="mrp-list__activity">
                  <div className="mrp-list__activity-main">
                    <div className="mrp-list__activity-label">
                      {dayCompare.latestDay
                        ? `Активность · ${formatDayNameShort(dayCompare.latestDay)}`
                        : 'Активность за день'}
                    </div>
                    <div className="mrp-list__activity-row">
                      <div className="mrp-list__activity-value">{dayCompare.currentScore}</div>
                      {dayCompare.hasEarlier ? (
                        <div className={`mrp-list__delta mrp-list__delta--${dayCompare.delta.direction}`} title="К среднему прошлых рабочих дней">
                          <span className="mrp-list__delta-icon" aria-hidden>
                            {dayCompare.delta.direction === 'up' ? '▲' : dayCompare.delta.direction === 'down' ? '▼' : '●'}
                          </span>
                          <span>{dayCompare.delta.diff > 0 ? '+' : ''}{dayCompare.delta.diff}</span>
                          <span className="mrp-list__delta-pct">
                            {dayCompare.delta.percent > 0 ? '+' : ''}{dayCompare.delta.percent}%
                          </span>
                        </div>
                      ) : (
                        <div className="mrp-list__delta mrp-list__delta--flat" title="Пока один рабочий день">
                          старт
                        </div>
                      )}
                    </div>
                    <div className="mrp-list__spark" title="Дни недели" aria-hidden>
                      {dayCompare.chronological.map((day) => (
                        <span
                          key={day.day}
                          className={`mrp-list__spark-bar ${day.day === dayCompare.latestDay ? 'is-current' : ''} ${day.score > 0 ? 'has-value' : ''}`}
                          style={{ height: `${Math.max(3, Math.round(((day.score || 0) / dayCompare.maxScore) * 16))}px` }}
                          title={`${formatDayNameShort(day.day)}: ${day.score}`}
                        />
                      ))}
                    </div>
                    <div className="mrp-list__activity-hint">* Новый день к ср. прошлых</div>
                  </div>
                  <div className="mrp-list__activity-rail-wrap">
                    <button type="button" className="mrp-list__rail-btn" onClick={() => scrollActivityRail(-1)} aria-label="Листать метрики влево">‹</button>
                    <div className="mrp-list__activity-rail" ref={activityRailRef} tabIndex={0}>
                      {dayCompare.cards.map((card) => (
                        <div key={card.key} className={`mrp-list__activity-card mrp-list__activity-card--${card.group} ${card.value > 0 ? 'has-value' : ''}`} title={`${card.label} · к ср. прошлых дней`}>
                          <div className="mrp-list__activity-card-top">
                            <span className="mrp-list__activity-card-label">{card.label}</span>
                            <span className={`mrp-list__card-delta mrp-list__card-delta--${card.delta.direction}`}>
                              {card.delta.diff === 0 ? '0' : `${card.delta.direction === 'up' ? '▲' : '▼'}${card.delta.diff > 0 ? '+' : ''}${card.delta.diff}`}
                            </span>
                          </div>
                          <div className="mrp-list__activity-card-mid">
                            <span className="mrp-list__activity-card-value">{card.value > 0 ? card.value : '—'}</span>
                            {card.share > 0 && <span className="mrp-list__activity-card-share">{card.share}%</span>}
                          </div>
                          <div className="mrp-list__activity-card-bars" aria-hidden>
                            {card.series.map((value, index) => (
                              <span
                                key={`${card.key}-${index}`}
                                className={`mrp-list__activity-card-bar ${index + 1 === dayCompare.latestDay ? 'is-current' : ''} ${value > 0 ? 'has-value' : ''}`}
                                style={{ height: `${Math.max(2, Math.round((value / card.max) * 10))}px` }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="mrp-list__rail-btn" onClick={() => scrollActivityRail(1)} aria-label="Листать метрики вправо">›</button>
                  </div>
                </div>
              )}
            </div>

            <div className="mrp-list__table-wrap">
              <table className="mrp-list__table">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Дата</th>
                    {listViewMetrics.map((metric) => <th key={metric.key}>{metric.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(weekStats.dailyStats || []).map((dayStat) => (
                    <tr key={dayStat.day} className={isVacationDay(selectedModerator.discord_id, dayStat.date) ? 'is-vacation' : ''}>
                      <td className="mrp-list__day">{formatDayNameShort(dayStat.day)}</td>
                      <td className="mrp-list__date">{formatDayDate(dayStat.date)}</td>
                      {listViewMetrics.map((metric) => (
                        <td key={metric.key}>{renderMetricCellList(selectedModerator.discord_id, dayStat, metric)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="mrp-list__total-spacer" aria-hidden="true" />
                    <td className="mrp-list__total-label">Итого</td>
                    {listViewMetrics.map((metric) => {
                      const total = getMetricTotal(weekStats, metric.key);
                      return (
                        <td key={metric.key} className="mrp-list__total-val">
                          <span className={`mrp-list__sum ${total > 0 ? 'has-value' : ''}`}>{total > 0 ? total : '—'}</span>
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mrp-list__mobile">
              {(weekStats.dailyStats || []).map((dayStat) => (
                <div key={dayStat.day} className={`mrp-list__card ${isVacationDay(selectedModerator.discord_id, dayStat.date) ? 'is-vacation' : ''}`}>
                  <div className="mrp-list__card-head">
                    {formatDayName(dayStat.day)}
                    <span>{formatDayDate(dayStat.date)}</span>
                  </div>
                  <div className="mrp-list__card-rows">
                    {listViewMetrics.map((metric) => (
                      <div key={metric.key} className="mrp-list__card-row">
                        <span>{metric.label}</span>
                        {renderMetricCellList(selectedModerator.discord_id, dayStat, metric)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="mrp-list__card mrp-list__card--totals">
                <div className="mrp-list__card-head">Итого за неделю</div>
                <div className="mrp-list__card-rows">
                  {listViewMetrics.map((metric) => {
                    const total = getMetricTotal(weekStats, metric.key);
                    return (
                      <div key={metric.key} className="mrp-list__card-row">
                        <span>{metric.label}</span>
                        <span className={`mrp-list__sum ${total > 0 ? 'has-value' : ''}`}>{total > 0 ? total : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {displayMode === 'table' && !isMobile && (
          <div className="statistics-mrp-table-view">
            {loadingTable ? (
              <div className="statistics-mrp-loading">
                <div className="loader" />
                <p>Загрузка данных...</p>
              </div>
            ) : (
              <StatisticsManagersTable
                title="Junior"
                moderators={tableModerators}
                metrics={getMetricsForSection(false)}
                {...tableMetricProps}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default StatisticsJunior;
